import React, { useState, useRef, useEffect } from 'react';
import { McButton, McIcon } from '@maersk-global/mds-react-wrapper';
import { downloadAsCSV, downloadAsJSON, downloadAsSql } from '../../utils/downloadUtils';

interface DownloadResultsButtonProps {
  data: any[];
  queryId?: string;
  sqlQuery?: string;
}

/**
 * A download button with a dropdown menu offering CSV and JSON export options.
 * Positioned top-right above the results table.
 */
export const DownloadResultsButton: React.FC<DownloadResultsButtonProps> = ({ data, queryId, sqlQuery }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!data || data.length === 0) return null;

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <McButton
        variant="outlined"
        appearance="primary"
        fit="small"
        onClick={() => setOpen(prev => !prev)}
        title="Download results"
      >
        <McIcon icon="tray-arrow-down" slot="prefix" />
        Download
      </McButton>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 200,
            background: 'var(--mds-color-white-background, #fff)',
            border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => { downloadAsCSV(data, queryId); setOpen(false); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: 'var(--mds-color-neutral-strong-text, #333)',
              textAlign: 'left',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--mds-color-neutral-weakest-background, #f5f5f5)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <McIcon icon="file" style={{ fontSize: 16, color: 'var(--mds-color-primary-main-decoration, #0073ab)' }} />
            Download as CSV
          </button>
          <div style={{ height: 1, background: 'var(--mds-color-neutral-weakest-decoration, #eee)' }} />
          <button
            onClick={() => { downloadAsJSON(data, queryId); setOpen(false); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: 'var(--mds-color-neutral-strong-text, #333)',
              textAlign: 'left',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--mds-color-neutral-weakest-background, #f5f5f5)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <McIcon icon="file" style={{ fontSize: 16, color: 'var(--mds-color-primary-main-decoration, #0073ab)' }} />
            Download as JSON
          </button>
          {sqlQuery && sqlQuery.trim() && (
            <>
              <div style={{ height: 1, background: 'var(--mds-color-neutral-weakest-decoration, #eee)' }} />
              <button
                onClick={() => { downloadAsSql(sqlQuery, queryId); setOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--mds-color-neutral-strong-text, #333)',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--mds-color-neutral-weakest-background, #f5f5f5)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <McIcon icon="pencil" style={{ fontSize: 16, color: 'var(--mds-color-primary-main-decoration, #0073ab)' }} />
                Download as SQL
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
